import * as THREE from 'three';
import { SpellType } from '../game/CooldownManager';

export interface SpellConfig {
  type: SpellType;
  damage: number;
  speed: number;
  color: THREE.Color;
  particleCount: number;
  chargeTime?: number; // Time in seconds before spell launches
}

// Callback type for effects integration
export type SpellEventCallback = (spell: Spell, event: string, data?: Record<string, unknown>) => void;

export abstract class Spell {
  protected mesh: THREE.Group;
  protected particles!: THREE.Points;
  protected velocity: THREE.Vector3;
  protected isActive: boolean = true;
  protected hasImpacted: boolean = false;
  protected config: SpellConfig;
  protected scene: THREE.Scene;
  protected target: THREE.Vector3;
  protected startPosition: THREE.Vector3;
  protected isPlayerSpell: boolean;
  protected disposed: boolean = false;
  protected impactPosition: THREE.Vector3 = new THREE.Vector3();

  // Effects integration
  private eventCallback: SpellEventCallback | null = null;

  // Charging state
  protected isCharging: boolean = false;
  protected chargeElapsed: number = 0;
  protected chargeIndicator: THREE.Group | null = null;

  // Live target reference for dynamic hit detection (tracks moving wizards)
  protected liveTarget: THREE.Vector3 | null = null;

  constructor(scene: THREE.Scene, startPos: THREE.Vector3, targetPos: THREE.Vector3, config: SpellConfig, isPlayerSpell: boolean = true) {
    this.scene = scene;
    this.config = config;
    this.startPosition = startPos.clone();
    this.target = targetPos.clone();
    this.isPlayerSpell = isPlayerSpell;
    this.mesh = new THREE.Group();
    this.mesh.position.copy(startPos);

    this.velocity = new THREE.Vector3()
      .subVectors(targetPos, startPos)
      .normalize()
      .multiplyScalar(config.speed);

    // If spell has charge time, start in charging state
    if (config.chargeTime && config.chargeTime > 0) {
      this.isCharging = true;
      this.createChargeIndicator();
      this.mesh.visible = false; // Hide main spell during charge
      // Note: chargeStart event is emitted on first update() so callback can be set after construction
    }

    this.particles = this.createParticles();
    this.createSpellVisual();

    this.scene.add(this.mesh);
  }

  protected createChargeIndicator(): void {
    this.chargeIndicator = new THREE.Group();
    this.chargeIndicator.position.copy(this.startPosition);

    // Create pulsing ring to indicate charging
    const ringGeometry = new THREE.RingGeometry(0.3, 0.5, 32);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: this.config.color,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = -Math.PI / 2;
    this.chargeIndicator.add(ring);

    // Create growing sphere
    const sphereGeometry = new THREE.SphereGeometry(0.2, 16, 16);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: this.config.color,
      transparent: true,
      opacity: 0.6
    });
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphere.name = 'chargeSphere';
    this.chargeIndicator.add(sphere);

    this.scene.add(this.chargeIndicator);
  }

  protected updateChargeIndicator(progress: number): void {
    if (!this.chargeIndicator) return;

    // Pulse the ring
    const ring = this.chargeIndicator.children[0] as THREE.Mesh;
    if (ring) {
      ring.scale.setScalar(1 + Math.sin(this.chargeElapsed * 10) * 0.2);
      (ring.material as THREE.MeshBasicMaterial).opacity = 0.5 + progress * 0.5;
    }

    // Grow the sphere based on progress
    const sphere = this.chargeIndicator.getObjectByName('chargeSphere') as THREE.Mesh;
    if (sphere) {
      sphere.scale.setScalar(0.5 + progress * 1.5);
      (sphere.material as THREE.MeshBasicMaterial).opacity = 0.3 + progress * 0.5;
    }
  }

  protected disposeChargeIndicator(): void {
    if (this.chargeIndicator) {
      this.scene.remove(this.chargeIndicator);
      this.chargeIndicator.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) {
            child.material.dispose();
          }
        }
      });
      this.chargeIndicator = null;
    }
  }

  protected abstract createSpellVisual(): void;
  protected abstract createParticles(): THREE.Points;
  public abstract createImpactEffect(): void;

  update(delta: number): boolean {
    if (!this.isActive || this.disposed) return false;

    // Handle charging phase
    if (this.isCharging) {
      // Emit chargeStart on first frame (after callback can be attached)
      if (this.chargeElapsed === 0) {
        this.emitEvent('chargeStart', { position: this.startPosition.toArray(), color: this.config.color.getHex() });
      }
      this.chargeElapsed += delta;
      const chargeTime = this.config.chargeTime || 1;
      const progress = Math.min(this.chargeElapsed / chargeTime, 1);

      this.updateChargeIndicator(progress);
      this.emitEvent('chargeUpdate', { progress, position: this.startPosition.toArray() });

      // Charge complete - launch spell
      if (this.chargeElapsed >= chargeTime) {
        this.isCharging = false;
        this.mesh.visible = true;
        this.disposeChargeIndicator();
        this.emitEvent('chargeDone');
      }

      return true; // Still active during charging
    }

    this.mesh.position.add(this.velocity.clone().multiplyScalar(delta));
    this.updateParticles(delta);

    // Always save current position for impact effects
    this.impactPosition.copy(this.mesh.position);

    // Check hit against live target position (tracks moving wizard) or static target
    const hitTarget = this.liveTarget || this.target;
    const distanceToTarget = this.mesh.position.distanceTo(hitTarget);
    if (distanceToTarget < 2.5) {
      this.hasImpacted = true;
      this.isActive = false;
      return false;
    }

    // Also check if spell has traveled too far (missed)
    const distanceFromStart = this.mesh.position.distanceTo(this.startPosition);
    if (distanceFromStart > 25) {
      this.isActive = false;
      return false;
    }

    return true;
  }

  protected abstract updateParticles(delta: number): void;

  triggerImpact(): void {
    if (!this.disposed) {
      this.createImpactEffect();
      this.dispose();
    }
  }

  getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  getDamage(): number {
    return this.config.damage;
  }

  getType(): SpellType {
    return this.config.type;
  }

  isFromPlayer(): boolean {
    return this.isPlayerSpell;
  }

  didHit(): boolean {
    return this.hasImpacted;
  }

  getTarget(): THREE.Vector3 {
    return this.target.clone();
  }

  getColor(): THREE.Color {
    return this.config.color.clone();
  }

  getImpactPosition(): THREE.Vector3 {
    return this.impactPosition.clone();
  }

  /** Set a live target reference so hit detection tracks a moving wizard position */
  setLiveTarget(pos: THREE.Vector3): void {
    this.liveTarget = pos;
  }

  /** Register a callback for spell lifecycle events (chargeStart, chargeUpdate, chargeDone, impact) */
  setEventCallback(cb: SpellEventCallback): void {
    this.eventCallback = cb;
  }

  protected emitEvent(event: string, data?: Record<string, unknown>): void {
    if (this.eventCallback) {
      this.eventCallback(this, event, data);
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.disposeChargeIndicator();
    this.scene.remove(this.mesh);
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
      if (child instanceof THREE.Points) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
}
